import React from 'react';
import { T, FONT_STACK } from './login.theme';
import { CloseIcon, EyeIcon, EyeOffIcon, Spinner } from './LoginIcons';

/**
 * The handful of controls the login screen and its three dialogs share.
 *
 * Plain elements with inline styles, matching how the rest of this app is
 * built — no new dependency for four buttons and a text box.
 */

/* ── Field ──────────────────────────────────────────────────────────────── */

export function Field({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onEnter,
  placeholder,
  autoFocus,
  disabled,
  invalid,
  large,
}) {
  const [revealed, setRevealed] = React.useState(false);
  const isPassword = type === 'password';

  const borderColor = invalid ? T.danger : T.borderStrong;
  const ring = invalid ? `0 0 0 3px ${T.dangerTint}` : 'none';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label ? (
        <label
          htmlFor={name}
          style={{ fontSize: 11.5, fontWeight: 600, color: T.inkSoft, letterSpacing: 0.2 }}
        >
          {label}
        </label>
      ) : null}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          height: large ? 46 : T.fieldHeight,
          padding: '0 13px',
          background: disabled ? T.sunk : T.surface,
          border: `${invalid ? 1.5 : 1}px solid ${borderColor}`,
          borderRadius: T.radiusControl,
          boxShadow: ring,
          boxSizing: 'border-box',
        }}
      >
        <input
          id={name}
          name={name}
          type={isPassword && revealed ? 'text' : type}
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && onEnter) {
              event.preventDefault();
              onEnter();
            }
          }}
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            font: 'inherit',
            fontFamily: FONT_STACK,
            fontSize: large && isPassword && !revealed ? 16 : 13.5,
            letterSpacing: large && isPassword && !revealed ? 4 : 'normal',
            color: T.ink,
          }}
        />

        {isPassword ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            onClick={() => setRevealed((on) => !on)}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: 0,
              border: 'none',
              background: 'transparent',
              color: T.inkFaint,
              cursor: 'pointer',
            }}
          >
            {revealed ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* ── Buttons ────────────────────────────────────────────────────────────── */

const buttonBase = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 9,
  border: 'none',
  borderRadius: T.radiusControl,
  font: 'inherit',
  fontFamily: FONT_STACK,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export function PrimaryButton({ children, onClick, disabled, busy, type = 'button', style }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || busy}
      style={{
        ...buttonBase,
        width: '100%',
        height: T.fieldHeight,
        background: T.brand,
        color: T.surface,
        fontSize: 14,
        fontWeight: 700,
        opacity: disabled ? 0.45 : busy ? 0.72 : 1,
        cursor: disabled || busy ? 'not-allowed' : 'pointer',
        ...style,
      }}
    >
      {busy ? <Spinner /> : null}
      <span>{children}</span>
    </button>
  );
}

export function GhostButton({ children, onClick, disabled, style }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...buttonBase,
        height: T.fieldHeight,
        background: T.surface,
        border: `1px solid ${disabled ? T.border : T.borderStrong}`,
        color: disabled ? T.inkGhost : T.inkSoft,
        fontSize: 13.5,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function DangerButton({ children, onClick, disabled, busy, style }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      style={{
        ...buttonBase,
        height: T.fieldHeight,
        background: T.surface,
        border: `1px solid ${T.dangerBorder}`,
        color: T.danger,
        fontSize: 13.5,
        fontWeight: 700,
        opacity: busy ? 0.6 : 1,
        cursor: disabled || busy ? 'not-allowed' : 'pointer',
        ...style,
      }}
    >
      {busy ? <Spinner /> : null}
      <span>{children}</span>
    </button>
  );
}

/* ── Banner ─────────────────────────────────────────────────────────────── */

const TONES = {
  danger: { bg: T.dangerTint, border: T.dangerBorder, ink: T.danger },
  warning: { bg: T.pendingTint, border: T.pendingBorder, ink: T.pendingInk },
};

export function Banner({ tone = 'danger', icon, title, children }) {
  const palette = TONES[tone] || TONES.danger;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '12px 13px',
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: T.radiusControl,
      }}
    >
      {icon ? (
        <span style={{ display: 'flex', paddingTop: 1, color: palette.ink }}>{icon}</span>
      ) : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        {title ? (
          <div style={{ fontSize: 12.5, fontWeight: 700, color: T.ink }}>{title}</div>
        ) : null}
        {children ? (
          <div style={{ fontSize: 12, color: palette.ink, lineHeight: 1.5 }}>{children}</div>
        ) : null}
      </div>
    </div>
  );
}

/* ── Dialog ─────────────────────────────────────────────────────────────── */

/**
 * A centred card over a dim. Escape closes it, but never while a request is
 * in flight — cancelling a check halfway leaves the caller guessing.
 */
export function Dialog({ children, onClose, width = T.cardWidth, locked }) {
  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !locked) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, locked]);

  return (
    <div
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !locked) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: T.backdrop,
      }}
    >
      <div
        style={{
          position: 'relative',
          width,
          maxWidth: 'calc(100vw - 32px)',
          background: T.surface,
          borderRadius: T.radiusDialog,
          boxShadow: T.dialog,
          boxSizing: 'border-box',
        }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          disabled={locked}
          style={{
            position: 'absolute',
            right: 14,
            top: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 30,
            height: 30,
            border: 'none',
            borderRadius: T.radiusControl,
            background: 'transparent',
            color: locked ? T.inkGhost : T.inkFaint,
            cursor: locked ? 'not-allowed' : 'pointer',
          }}
        >
          <CloseIcon />
        </button>
        {children}
      </div>
    </div>
  );
}
